import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Input,
  Spinner,
  Badge,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular, PersonRegular, CalendarRegular, MailRegular, PhoneRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn } from '../../../types';
import { getTaxReturns } from '../../../services/api';

export interface MergeSpouseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTaxReturn: TaxReturn;
  mergeSpouseMutation: UseMutationResult<TaxReturn, Error, string>;
}

export function MergeSpouseDialog({
  open,
  onOpenChange,
  currentTaxReturn,
  mergeSpouseMutation,
}: MergeSpouseDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<TaxReturn[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<TaxReturn | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedReturn(null);
      setConfirmStep(false);
    }
  }, [open]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const returns = await getTaxReturns({ taxYear: currentTaxReturn.taxYear });
      // Filter: match by client name, exclude current return, exclude inactive/cancelled, exclude business returns
      const filtered = returns.filter(r =>
        r.id !== currentTaxReturn.id &&
        r.status !== 'inactive' &&
        !r.isCancelled &&
        r.returnType === 'individual' &&
        r.clientName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(filtered);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchTerm, currentTaxReturn.id, currentTaxReturn.taxYear]);

  const handleSelect = (taxReturn: TaxReturn) => {
    setSelectedReturn(taxReturn);
    setConfirmStep(true);
  };

  const handleConfirm = () => {
    if (!selectedReturn) return;
    mergeSpouseMutation.mutate(selectedReturn.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handleBack = () => {
    setConfirmStep(false);
    setSelectedReturn(null);
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '560px' }}>
        <DialogBody>
          <DialogTitle>Merge Return as Spouse</DialogTitle>
          <DialogContent>
            {!confirmStep ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                  Search for a tax return to merge as the spouse on this return.
                  The selected client will become the spouse, and their return will be marked inactive.
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Input
                    style={{ flex: 1 }}
                    placeholder="Search by client name..."
                    value={searchTerm}
                    onChange={(_, data) => setSearchTerm(data.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    contentBefore={<SearchRegular />}
                  />
                  <Button
                    appearance="primary"
                    onClick={handleSearch}
                    disabled={searching || !searchTerm.trim()}
                  >
                    {searching ? <Spinner size="tiny" /> : 'Search'}
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div style={{ maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {searchResults.map((ret) => (
                      <div
                        key={ret.id}
                        onClick={() => handleSelect(ret)}
                        style={{
                          padding: '12px',
                          backgroundColor: 'var(--tl-bg-page)',
                          border: '1px solid var(--tl-border)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e8f0fe')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl-bg-page)')}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <PersonRegular style={{ fontSize: tokens.fontSizeBase400, color: '#0078d4' }} />
                          <span style={{ fontWeight: 600, fontSize: tokens.fontSizeBase300 }}>{ret.clientName}</span>
                          <Badge size="small" appearance="outline">{ret.taxYear}</Badge>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', marginLeft: '24px' }}>
                          {ret.clientEmail && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <MailRegular style={{ fontSize: tokens.fontSizeBase200 }} /> {ret.clientEmail}
                            </span>
                          )}
                          {ret.clientPhone && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <PhoneRegular style={{ fontSize: tokens.fontSizeBase200 }} /> {ret.clientPhone}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.length === 0 && searchTerm && !searching && (
                  <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', textAlign: 'center', padding: '16px' }}>
                    No matching returns found for "{searchTerm}".
                  </div>
                )}
              </div>
            ) : selectedReturn && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f0f6ff',
                  borderRadius: '8px',
                  border: '1px solid #b4d6fa',
                }}>
                  <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '12px' }}>
                    Confirm Spouse Merge
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <PersonRegular style={{ color: '#0078d4' }} />
                      <span style={{ fontWeight: 500 }}>{selectedReturn.clientName}</span>
                      <span style={{ color: 'var(--tl-text-secondary)', fontSize: '13px' }}>will become the spouse</span>
                    </div>
                    {selectedReturn.clientEmail && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '24px', fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                        <MailRegular style={{ fontSize: tokens.fontSizeBase300 }} /> {selectedReturn.clientEmail}
                      </div>
                    )}
                    {selectedReturn.clientPhone && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '24px', fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                        <PhoneRegular style={{ fontSize: tokens.fontSizeBase300 }} /> {selectedReturn.clientPhone}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '24px', fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                      <CalendarRegular style={{ fontSize: tokens.fontSizeBase300 }} /> Tax Year {selectedReturn.taxYear}
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff8e6',
                  borderRadius: '8px',
                  border: '1px solid #f5c518',
                  fontSize: '13px',
                }}>
                  <strong>This will:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Add <strong>{selectedReturn.clientName}</strong> as the spouse on <strong>{currentTaxReturn.clientName}</strong>'s return</li>
                    <li>Change the filing type to <strong>Married Filing Jointly</strong></li>
                    <li>Mark <strong>{selectedReturn.clientName}</strong>'s separate return as <strong>inactive</strong></li>
                  </ul>
                </div>

                {mergeSpouseMutation.isError && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fdf2f2',
                    borderRadius: '6px',
                    border: '1px solid #d83b01',
                    fontSize: '13px',
                    color: '#d83b01',
                  }}>
                    Error: {mergeSpouseMutation.error?.message || 'Failed to merge spouse'}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {confirmStep ? (
              <>
                <Button appearance="secondary" onClick={handleBack} disabled={mergeSpouseMutation.isPending}>
                  Back
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleConfirm}
                  disabled={mergeSpouseMutation.isPending}
                >
                  {mergeSpouseMutation.isPending ? 'Merging...' : 'Confirm Merge'}
                </Button>
              </>
            ) : (
              <Button appearance="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
